"use client";

import { Api } from "@/app/api";
import type { ServerCollectionResponse } from "@/types/businessTypes";
import {
	packEmailHtml,
	packSequenceStep,
	packSequenceUpsert,
} from "@/lib/utils/waf-safe-email-body";

export type CrmLeadStage =
	| "new"
	| "contacted"
	| "qualified"
	| "proposal"
	| "won"
	| "lost"
	| "unqualified"
	| "low_data"
	| "in_review";

export type CrmLeadSource = "companies_house" | "manual" | "other";

export type CrmLead = {
	_id: string;
	companyName: string;
	companyNumber?: string;
	companyStatus?: string;
	companyType?: string;
	location?: string;
	sicCodes?: string[];
	searchKeyword?: string;
	dateOfIncorporation?: string;
	accountsType?: string;
	directorsSummary?: string;
	address?: string;
	website?: string;
	contactName?: string;
	contactFirstName?: string;
	contactLastName?: string;
	contactEmail?: string;
	contactPhone?: string;
	pscSummary?: string;
	stage: CrmLeadStage;
	source: CrmLeadSource;
	notes?: string;
	aiContactSearch?: CrmAiContactSearchSnapshot;
	quickPromoCode?: string;
	quickPromoCodeEndsAt?: string;
	createdAt?: string;
	updatedAt?: string;
};

export type CreateCrmLeadBody = {
	companyName: string;
	companyNumber?: string;
	companyStatus?: string;
	companyType?: string;
	location?: string;
	sicCodes?: string[];
	searchKeyword?: string;
	dateOfIncorporation?: string;
	accountsType?: string;
	directorsSummary?: string;
	address?: string;
	website?: string;
	contactName?: string;
	contactFirstName?: string;
	contactLastName?: string;
	contactEmail?: string;
	contactPhone?: string;
	pscSummary?: string;
	stage?: CrmLeadStage;
	source?: CrmLeadSource;
	notes?: string;
	aiContactSearch?: CrmAiContactSearchSnapshot;
	quickPromoCode?: string;
	quickPromoCodeEndsAt?: string;
};

export type CrmAiContactSource = {
	title: string;
	url: string;
	site?: string;
};

export type CrmAiContactCandidate = {
	email?: string | null;
	phone?: string | null;
	website?: string | null;
	linkedinUrl?: string | null;
	instagramUrl?: string | null;
	facebookUrl?: string | null;
	tiktokUrl?: string | null;
	googleBusinessUrl?: string | null;
	confidence: "high" | "medium" | "low";
	locationMatch: boolean;
	matchedLocationNote?: string | null;
	evidence?: string | null;
	sources?: CrmAiContactSource[];
};

export type CrmAiContactSearchResult = {
	found: boolean;
	summary: string;
	best: CrmAiContactCandidate | null;
	alternatives: CrmAiContactCandidate[];
	searchNotes?: string;
	groundingSources: CrmAiContactSource[];
	webSearchQueries?: string[];
};

export type SequencePromoOffer = {
	discountType: "PERCENTAGE" | "FIXED_AMOUNT";
	discountValue: number;
	currency: string;
	applyTo: string[];
	packageTiers: string[];
	validDays: number;
	minOrderAmount: number | null;
	applyDiscountToDueTodayFirst: boolean;
	stackableWithOtherPromoCodes: boolean;
	newUsersOnly: boolean;
};

export type CrmEmailSequenceStep = {
	subject: string;
	body: string;
	delayDays: number;
	includePromo?: boolean;
	promo?: SequencePromoOffer;
};

export type CrmEmailSendWindow = {
	weekday: number;
	time: string;
};

export type CrmEmailSequenceBroadcast = {
	_id: string;
	sequenceId: string;
	subject: string;
	body: string;
	sendAt: string;
	status: "scheduled" | "sending" | "sent" | "cancelled" | "failed";
	sentAt?: string;
	sentCount?: number;
	skippedCount?: number;
	failureReason?: string;
	includePromo?: boolean;
	promo?: SequencePromoOffer;
	createdAt?: string;
};

export type CrmEmailSequence = {
	_id: string;
	name: string;
	keyword: string;
	active: boolean;
	steps: CrmEmailSequenceStep[];
	sendWindows?: CrmEmailSendWindow[];
	defaultPromo?: SequencePromoOffer;
	broadcasts?: CrmEmailSequenceBroadcast[];
	activeEnrollmentCount?: number;
	stats?: {
		people: number;
		unsubscribed: number;
		failed: number;
		bounced: number;
		opened: number;
		clicked?: number;
		promoIssued?: number;
		promoRedeemed?: number;
		emails?: {
			stepIndex: number;
			sent: number;
			opened: number;
			clicked?: number;
			promoIssued?: number;
			promoRedeemed?: number;
		}[];
	};
};

export type UpsertCrmEmailSequenceBody = {
	name: string;
	keyword: string;
	active?: boolean;
	steps: CrmEmailSequenceStep[];
	sendWindows?: CrmEmailSendWindow[];
	defaultPromo?: SequencePromoOffer;
};

export type PatchCrmEmailSequenceBody = {
	name?: string;
	keyword?: string;
	active?: boolean;
	sendWindows?: CrmEmailSendWindow[];
	defaultPromo?: SequencePromoOffer;
};

export type CrmEmailSequenceDelivery = {
	stepIndex: number;
	subject: string;
	status: string;
	sentAt?: string;
	openedAt?: string;
	clickedAt?: string;
	failureReason?: string;
	issuedPromoCode?: string;
	issuedPromoExpiresAt?: string;
	promoRedeemed?: boolean;
};

export type CrmEmailSequenceEnrollment = {
	_id: string;
	sequenceId: string;
	leadId: string;
	status: "active" | "finished" | "stopped";
	nextStepIndex: number;
	nextSendAt?: string | null;
	stoppedReason?: string;
	companyName: string;
	contactEmail: string;
	contactName?: string;
	contactFirstName?: string;
	contactLastName?: string;
	/** Prefer this in UI: first + last, else contact/company name, else email. */
	displayName?: string;
	unsubscribed?: boolean;
	deliveries?: CrmEmailSequenceDelivery[];
};

/** Persisted on the lead after AI search / apply + save. */
export type CrmAiContactSearchSnapshot = CrmAiContactSearchResult & {
	searchedAt?: string;
	appliedAt?: string;
};

export type CrmLeadEmailStatus =
	| "queued"
	| "scheduled"
	| "sent"
	| "failed"
	| "delivered"
	| "bounced"
	| "opened"
	| "cancelled";

export type CrmLeadEmail = {
	_id: string;
	leadId: string;
	to: string;
	cc?: string;
	from: string;
	subject: string;
	body: string;
	signature?: string;
	signatureHtml?: string;
	signatureImageUrl?: string;
	includeSignature: boolean;
	status: CrmLeadEmailStatus;
	sesMessageId?: string;
	sentAt?: string;
	scheduledFor?: string;
	deliveredAt?: string;
	openedAt?: string;
	failureReason?: string;
	createdAt?: string;
	updatedAt?: string;
};

export type SendCrmLeadEmailBody = {
	to: string;
	cc?: string;
	subject: string;
	body: string;
	includeSignature?: boolean;
	signature?: string;
	signatureImageUrl?: string;
	/** ISO datetime — omit to send immediately */
	scheduledFor?: string;
};

export type CrmLeadWhatsApp = {
	_id: string;
	leadId: string;
	to: string;
	body: string;
	sentAt?: string;
	createdAt?: string;
	updatedAt?: string;
};

export type LogCrmLeadWhatsAppBody = {
	to: string;
	body: string;
};

export type CrmEmailSettings = {
	from: string;
	fromName: string;
	replyTo: string;
	defaultSignature: string;
	signatureImageUrl: string;
	signatureHtml: string;
	brandColor: string;
};

export type CrmAiContactSearchBody = {
	companyName: string;
	companyNumber?: string;
	location?: string;
	address?: string;
	searchKeyword?: string;
	directorsSummary?: string;
	contactName?: string;
	companyType?: string;
	sicCodes?: string;
};

export type CrmLeadCollection = ServerCollectionResponse<CrmLead>;

export const crmApiInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		searchCrmLeads: builder.query<
			CrmLeadCollection,
			{
				page?: number;
				limit?: number;
				search?: string;
				stage?: string;
				sortBy?: string;
			}
		>({
			query: ({ page = 1, limit = 10, search, stage, sortBy }) => ({
				url: "/crm/leads",
				params: {
					page,
					limit,
					search: search || undefined,
					stage: stage && stage !== "all" ? stage : undefined,
					sortBy: sortBy ?? "updatedAt:desc",
				},
			}),
			providesTags: ["Crm"],
		}),

		getCrmStageCounts: builder.query<Record<string, number>, void>({
			query: () => ({ url: "/crm/leads/stage-counts" }),
			providesTags: ["Crm"],
		}),

		getCrmLead: builder.query<CrmLead, string>({
			query: (id) => ({
				url: `/crm/leads/${encodeURIComponent(id)}`,
			}),
			providesTags: ["Crm"],
		}),

		createCrmLead: builder.mutation<CrmLead, CreateCrmLeadBody>({
			query: (body) => ({
				url: "/crm/leads",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		upsertCrmLeadFromCompaniesHouse: builder.mutation<
			{ lead: CrmLead; created: boolean },
			CreateCrmLeadBody
		>({
			query: (body) => ({
				url: "/crm/leads/from-companies-house",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		matchCrmCompanyNumbers: builder.mutation<
			{ companyNumbers: string[] },
			{ companyNumbers: string[] }
		>({
			query: (body) => ({
				url: "/crm/leads/match-company-numbers",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
		}),

		bulkUpsertCrmLeadsFromCompaniesHouse: builder.mutation<
			{
				created: number;
				skipped: number;
				createdNumbers: string[];
				skippedNumbers: string[];
				errors: Array<{ companyNumber?: string; message: string }>;
			},
			{ leads: CreateCrmLeadBody[] }
		>({
			query: (body) => ({
				url: "/crm/leads/bulk-from-companies-house",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		resyncCrmLeadsFromCompaniesHouse: builder.mutation<
			{
				updated: number;
				missing: number;
				updatedNumbers: string[];
				missingNumbers: string[];
				errors: Array<{ companyNumber?: string; message: string }>;
			},
			{ leads: CreateCrmLeadBody[] }
		>({
			query: (body) => ({
				url: "/crm/leads/resync-from-companies-house",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		listGoogleDataReview: builder.query<
			{
				keyword: string;
				location: string;
				results: Array<{
					id: string;
					name: string;
					firstName?: string;
					lastName?: string;
					keyword?: string;
					area?: string;
					address: string;
					email: string;
					phone: string;
					website: string;
					profileUrl: string;
					status: "new" | "in_review";
				}>;
			},
			{ keyword?: string; location?: string; all?: boolean }
		>({
			query: ({ keyword = "", location = "", all }) => ({
				url: all
					? "/crm/leads/google-data-review?all=1"
					: `/crm/leads/google-data-review?keyword=${encodeURIComponent(keyword)}&location=${encodeURIComponent(location)}`,
			}),
			providesTags: ["Crm"],
		}),

		searchGoogleBusinessLeads: builder.mutation<
			{
				keyword: string;
				location: string;
				excludeWithWebsite: boolean;
				skipPhoneWithoutEmail: boolean;
				limit: number;
				imported: number;
				importedIds?: string[];
				skippedWithWebsite?: number;
				results: Array<{
					id: string;
					name: string;
					firstName?: string;
					lastName?: string;
					keyword?: string;
					area?: string;
					address: string;
					email: string;
					phone: string;
					website: string;
					profileUrl: string;
					status: "new" | "in_review";
				}>;
			},
			{
				keyword: string;
				location: string;
				excludeWithWebsite?: boolean;
				skipPhoneWithoutEmail?: boolean;
				limit?: number;
			}
		>({
			query: (body) => ({
				url: "/crm/leads/google-business-search",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		deleteGoogleDataReviews: builder.mutation<{ deleted: number }, { ids: string[] }>({
			query: ({ ids }) => ({
				url: "/crm/leads/google-data-review/bulk-delete",
				method: "POST",
				body: JSON.stringify({ ids }),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		updateGoogleDataReviewStatus: builder.mutation<
			{ id: string; status: "new" | "in_review"; firstName?: string; lastName?: string },
			{ id: string; status?: "new" | "in_review"; firstName?: string; lastName?: string }
		>({
			query: ({ id, status, firstName, lastName }) => ({
				url: `/crm/leads/google-data-review/${encodeURIComponent(id)}`,
				method: "PATCH",
				body: JSON.stringify({ status, firstName, lastName }),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		aiContactSearchCrmLead: builder.mutation<
			CrmAiContactSearchResult,
			CrmAiContactSearchBody
		>({
			query: (body) => ({
				url: "/crm/leads/ai-contact-search",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
		}),

		listCrmLeadEmails: builder.query<CrmLeadEmail[], string>({
			query: (id) => ({
				url: `/crm/leads/${encodeURIComponent(id)}/emails`,
			}),
			providesTags: ["Crm"],
		}),

		getCrmEmailSettings: builder.query<CrmEmailSettings, void>({
			query: () => ({ url: "/crm/email-settings" }),
			providesTags: ["Crm"],
		}),

		updateCrmEmailSettings: builder.mutation<CrmEmailSettings, { signatureHtml: string }>({
			query: (body) => ({
				url: "/crm/email-settings",
				method: "PATCH",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		testCrmEmailSignature: builder.mutation<
			{ ok: boolean; to: string },
			{ to: string; signatureHtml?: string }
		>({
			query: (body) => ({
				url: "/crm/email-settings/test",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
		}),

		sendCrmLeadEmail: builder.mutation<
			CrmLeadEmail,
			{ leadId: string; body: SendCrmLeadEmailBody }
		>({
			query: ({ leadId, body }) => ({
				url: `/crm/leads/${encodeURIComponent(leadId)}/emails`,
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		cancelCrmLeadEmail: builder.mutation<
			CrmLeadEmail,
			{ leadId: string; emailId: string }
		>({
			query: ({ leadId, emailId }) => ({
				url: `/crm/leads/${encodeURIComponent(leadId)}/emails/${encodeURIComponent(emailId)}/cancel`,
				method: "POST",
			}),
			invalidatesTags: ["Crm"],
		}),

		listCrmLeadWhatsApp: builder.query<CrmLeadWhatsApp[], string>({
			query: (id) => ({
				url: `/crm/leads/${encodeURIComponent(id)}/whatsapp`,
			}),
			providesTags: ["Crm"],
		}),

		logCrmLeadWhatsApp: builder.mutation<
			CrmLeadWhatsApp,
			{ leadId: string; body: LogCrmLeadWhatsAppBody }
		>({
			query: ({ leadId, body }) => ({
				url: `/crm/leads/${encodeURIComponent(leadId)}/whatsapp`,
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		bulkUpdateCrmLeadStage: builder.mutation<
			{ matched: number; updated: number; stage: CrmLeadStage },
			{ leadIds: string[]; stage: CrmLeadStage }
		>({
			query: (body) => ({
				url: "/crm/leads/bulk-stage",
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		updateCrmLead: builder.mutation<
			CrmLead,
			{ id: string; body: Partial<CreateCrmLeadBody> }
		>({
			query: ({ id, body }) => ({
				url: `/crm/leads/${encodeURIComponent(id)}`,
				method: "PATCH",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		deleteCrmLead: builder.mutation<{ deleted: true }, string>({
			query: (id) => ({
				url: `/crm/leads/${encodeURIComponent(id)}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Crm"],
		}),

		listCrmEmailSequences: builder.query<CrmEmailSequence[], void>({
			query: () => ({ url: "/crm/sequences" }),
			providesTags: ["Crm"],
		}),

		createCrmEmailSequence: builder.mutation<
			CrmEmailSequence,
			UpsertCrmEmailSequenceBody
		>({
			queryFn: async (body, _api, _extra, baseQuery) => {
				const result = await baseQuery({
					url: "/crm/sequences",
					method: "POST",
					body: JSON.stringify(await packSequenceUpsert(body)),
					headers: { "Content-Type": "application/json" },
				});
				if (result.error) return { error: result.error };
				return { data: result.data as CrmEmailSequence };
			},
			invalidatesTags: ["Crm"],
		}),

		updateCrmEmailSequence: builder.mutation<
			CrmEmailSequence,
			{ id: string; body: PatchCrmEmailSequenceBody }
		>({
			query: ({ id, body }) => ({
				url: `/crm/sequences/${encodeURIComponent(id)}`,
				method: "PATCH",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		addCrmEmailSequenceStep: builder.mutation<
			CrmEmailSequence,
			{ id: string; step: CrmEmailSequenceStep }
		>({
			queryFn: async ({ id, step }, _api, _extra, baseQuery) => {
				const result = await baseQuery({
					url: `/crm/sequences/${encodeURIComponent(id)}/steps`,
					method: "POST",
					body: JSON.stringify(await packSequenceStep(step)),
					headers: { "Content-Type": "application/json" },
				});
				if (result.error) return { error: result.error };
				return { data: result.data as CrmEmailSequence };
			},
			invalidatesTags: ["Crm"],
		}),

		updateCrmEmailSequenceStep: builder.mutation<
			CrmEmailSequence,
			{ id: string; stepIndex: number; step: CrmEmailSequenceStep }
		>({
			queryFn: async ({ id, stepIndex, step }, _api, _extra, baseQuery) => {
				const result = await baseQuery({
					url: `/crm/sequences/${encodeURIComponent(id)}/steps/${stepIndex}`,
					method: "PATCH",
					body: JSON.stringify(await packSequenceStep(step)),
					headers: { "Content-Type": "application/json" },
				});
				if (result.error) return { error: result.error };
				return { data: result.data as CrmEmailSequence };
			},
			invalidatesTags: ["Crm"],
		}),

		removeCrmEmailSequenceStep: builder.mutation<
			CrmEmailSequence,
			{ id: string; stepIndex: number }
		>({
			query: ({ id, stepIndex }) => ({
				url: `/crm/sequences/${encodeURIComponent(id)}/steps/${stepIndex}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Crm"],
		}),

		enrollCrmEmailSequence: builder.mutation<
			{ enrolled: number; restarted: number; skipped: number },
			{
				id: string;
				leadIds?: string[];
				mailingListEmails?: string[];
				googleDataIds?: string[];
				matchKeyword?: boolean;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `/crm/sequences/${encodeURIComponent(id)}/enroll`,
				method: "POST",
				body: JSON.stringify(body),
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: ["Crm"],
		}),

		listCrmEmailSequenceEnrollments: builder.query<
			CrmEmailSequenceEnrollment[],
			string
		>({
			query: (id) => ({
				url: `/crm/sequences/${encodeURIComponent(id)}/enrollments`,
			}),
			providesTags: ["Crm"],
		}),

		stopCrmEmailSequenceEnrollment: builder.mutation<
			{ _id: string },
			{ sequenceId: string; leadId: string }
		>({
			query: ({ sequenceId, leadId }) => ({
				url: `/crm/sequences/${encodeURIComponent(sequenceId)}/enrollments/${encodeURIComponent(leadId)}/stop`,
				method: "POST",
			}),
			invalidatesTags: ["Crm"],
		}),

		createCrmEmailSequenceBroadcast: builder.mutation<
			CrmEmailSequenceBroadcast,
			{
				sequenceId: string;
				subject: string;
				body: string;
				sendAt: string;
				includePromo?: boolean;
				promo?: SequencePromoOffer;
			}
		>({
			queryFn: async ({ sequenceId, ...body }, _api, _extra, baseQuery) => {
				const result = await baseQuery({
					url: `/crm/sequences/${encodeURIComponent(sequenceId)}/broadcasts`,
					method: "POST",
					body: JSON.stringify({ ...body, body: await packEmailHtml(body.body) }),
					headers: { "Content-Type": "application/json" },
				});
				if (result.error) return { error: result.error };
				return { data: result.data as CrmEmailSequenceBroadcast };
			},
			invalidatesTags: ["Crm"],
		}),

		cancelCrmEmailSequenceBroadcast: builder.mutation<
			CrmEmailSequenceBroadcast,
			{ sequenceId: string; broadcastId: string }
		>({
			query: ({ sequenceId, broadcastId }) => ({
				url: `/crm/sequences/${encodeURIComponent(sequenceId)}/broadcasts/${encodeURIComponent(broadcastId)}/cancel`,
				method: "POST",
			}),
			invalidatesTags: ["Crm"],
		}),

		deleteCrmEmailSequenceBroadcast: builder.mutation<
			{ deleted: true },
			{ sequenceId: string; broadcastId: string }
		>({
			query: ({ sequenceId, broadcastId }) => ({
				url: `/crm/sequences/${encodeURIComponent(sequenceId)}/broadcasts/${encodeURIComponent(broadcastId)}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Crm"],
		}),

		updateCrmEmailSequenceBroadcast: builder.mutation<
			CrmEmailSequenceBroadcast,
			{
				sequenceId: string;
				broadcastId: string;
				subject: string;
				body: string;
				sendAt?: string;
				includePromo?: boolean;
				promo?: SequencePromoOffer;
			}
		>({
			queryFn: async ({ sequenceId, broadcastId, ...body }, _api, _extra, baseQuery) => {
				const result = await baseQuery({
					url: `/crm/sequences/${encodeURIComponent(sequenceId)}/broadcasts/${encodeURIComponent(broadcastId)}`,
					method: "PATCH",
					body: JSON.stringify({ ...body, body: await packEmailHtml(body.body) }),
					headers: { "Content-Type": "application/json" },
				});
				if (result.error) return { error: result.error };
				return { data: result.data as CrmEmailSequenceBroadcast };
			},
			invalidatesTags: ["Crm"],
		}),

		testCrmEmailSequenceBroadcast: builder.mutation<
			{ ok: true; to: string; issuedPromoCode?: string; issuedPromoExpiresAt?: string },
			{ sequenceId: string; broadcastId: string; to?: string }
		>({
			query: ({ sequenceId, broadcastId, to }) => ({
				url: `/crm/sequences/${encodeURIComponent(sequenceId)}/broadcasts/${encodeURIComponent(broadcastId)}/test`,
				method: "POST",
				body: JSON.stringify(to ? { to } : {}),
				headers: { "Content-Type": "application/json" },
			}),
		}),

		testCrmEmailSequenceStep: builder.mutation<
			{ ok: true; to: string; issuedPromoCode?: string; issuedPromoExpiresAt?: string },
			{ sequenceId: string; stepIndex: number; to?: string }
		>({
			query: ({ sequenceId, stepIndex, to }) => ({
				url: `/crm/sequences/${encodeURIComponent(sequenceId)}/steps/${stepIndex}/test`,
				method: "POST",
				body: JSON.stringify(to ? { to } : {}),
				headers: { "Content-Type": "application/json" },
			}),
		}),
	}),
});

export const {
	useSearchCrmLeadsQuery,
	useGetCrmStageCountsQuery,
	useGetCrmLeadQuery,
	useCreateCrmLeadMutation,
	useUpsertCrmLeadFromCompaniesHouseMutation,
	useMatchCrmCompanyNumbersMutation,
	useBulkUpsertCrmLeadsFromCompaniesHouseMutation,
	useResyncCrmLeadsFromCompaniesHouseMutation,
	useListGoogleDataReviewQuery,
	useSearchGoogleBusinessLeadsMutation,
	useDeleteGoogleDataReviewsMutation,
	useUpdateGoogleDataReviewStatusMutation,
	useAiContactSearchCrmLeadMutation,
	useListCrmLeadEmailsQuery,
	useGetCrmEmailSettingsQuery,
	useUpdateCrmEmailSettingsMutation,
	useTestCrmEmailSignatureMutation,
	useSendCrmLeadEmailMutation,
	useCancelCrmLeadEmailMutation,
	useListCrmLeadWhatsAppQuery,
	useLogCrmLeadWhatsAppMutation,
	useBulkUpdateCrmLeadStageMutation,
	useUpdateCrmLeadMutation,
	useDeleteCrmLeadMutation,
	useListCrmEmailSequencesQuery,
	useCreateCrmEmailSequenceMutation,
	useUpdateCrmEmailSequenceMutation,
	useAddCrmEmailSequenceStepMutation,
	useUpdateCrmEmailSequenceStepMutation,
	useRemoveCrmEmailSequenceStepMutation,
	useEnrollCrmEmailSequenceMutation,
	useListCrmEmailSequenceEnrollmentsQuery,
	useStopCrmEmailSequenceEnrollmentMutation,
	useCreateCrmEmailSequenceBroadcastMutation,
	useCancelCrmEmailSequenceBroadcastMutation,
	useDeleteCrmEmailSequenceBroadcastMutation,
	useUpdateCrmEmailSequenceBroadcastMutation,
	useTestCrmEmailSequenceBroadcastMutation,
	useTestCrmEmailSequenceStepMutation,
} = crmApiInjection;
